import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';

const Home = () => {
  const [profile, setProfile] = useState({
    name: '...',
    role: '',
    styles: '',
    skills: '',
    avatar: '',
    availability: '',
    timezone: '',
    openTo: '',
    tools: [],
    cchiMatches: [],
  });

  const [topMatch, setTopMatch] = useState(null);
  const [recentProject, setRecentProject] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user?.id) return;
      const userId = session.user.id;

      const { data: userProfile, error: profileError } = await supabase
        .from('creator_profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (profileError) {
        console.error('Error fetching profile:', profileError.message);
        return;
      }

      const { data: matchesData, error: matchErr } = await supabase
        .from('creator_matches')
        .select(
          'user_id, matched_user_id, first_name, cchi_score, matched_at, role, avatar, shared_genres, shared_tools'
        )
        .eq('user_id', userId)
        .order('cchi_score', { ascending: false })
        .limit(5);

      if (matchErr) {
        console.error('Error fetching matches:', matchErr.message);
        return;
      }

      // ---- NEW: look up which matched users are internal, using creator_profiles ----
      let internalIds = new Set();
      if (Array.isArray(matchesData) && matchesData.length > 0) {
        const uniqueMatchedIds = [
          ...new Set(
            matchesData
              .map((m) => m.matched_user_id)
              .filter((id) => !!id)
          ),
        ];

        if (uniqueMatchedIds.length > 0) {
          const { data: internalProfiles, error: internalErr } = await supabase
            .from('creator_profiles')
            .select('user_id, is_internal')
            .in('user_id', uniqueMatchedIds)
            .eq('is_internal', true);

          if (internalErr) {
            console.error('Error fetching internal flags:', internalErr.message);
          } else if (Array.isArray(internalProfiles)) {
            internalIds = new Set(internalProfiles.map((p) => p.user_id));
          }
        }
      }
      // ---------------------------------------------------------------------------

      const fullMatches =
        matchesData
          // filter out roles you already don’t want to show
          ?.filter((match) => !['Admin', 'OTT Buyer'].includes(match.role))
          // NEW: filter out any matches whose creator_profile.is_internal = true
          .filter((match) => !internalIds.has(match.matched_user_id))
          .map((match) => ({
            user_id: match.matched_user_id,
            name: match.first_name || 'Unknown',
            role: match.role || 'Unknown',
            score: match.cchi_score,
            avatar: match.avatar || '/user.png',
            sharedGenres: match.shared_genres
              ? match.shared_genres.split(',').map((g) => g.trim())
              : [],
            sharedTools: match.shared_tools
              ? match.shared_tools.split(',').map((t) => t.trim())
              : [],
          })) || [];

      setProfile({
        name: userProfile.name || 'Unknown',
        role: userProfile.role || '',
        styles: userProfile.genres || '',
        skills: '2D Animation, Illustration',
        avatar: userProfile.profile_image_url || '/user.png',
        availability: userProfile.availability || '',
        timezone: userProfile.time_zone || '',
        openTo: userProfile.collab_style || '',
        tools: userProfile.tools
          ? userProfile.tools.split(',').map((t) => t.trim())
          : [],
        cchiMatches: fullMatches,
      });

      setTopMatch(fullMatches[0] || null);

      const { data: recentProjects, error: projectErr } = await supabase
        .from('projects')
        .select('id, title, role, updated_at')
        .eq('creator_id', userId)
        .order('updated_at', { ascending: false })
        .limit(1);

      if (!projectErr && recentProjects && recentProjects.length > 0) {
        setRecentProject(recentProjects[0]);
      }
    };

    fetchData();
  }, []);

  // --------- Derived UI state for Stage 1 vs Data-Rich states ---------
  const hasMatches =
    Array.isArray(profile.cchiMatches) && profile.cchiMatches.length > 0;

  const hasStrongMatches =
    hasMatches && profile.cchiMatches.some((m) => (m.score || 0) >= 5);

  const hasProfileBasics = Boolean(
    (profile.role && profile.role.trim() !== '') ||
      (profile.styles && profile.styles.trim() !== '') ||
      (Array.isArray(profile.tools) && profile.tools.length > 0) ||
      (profile.availability && profile.availability.trim() !== '') ||
      (profile.timezone && profile.timezone.trim() !== '') ||
      (profile.openTo && profile.openTo.trim() !== '')
  );

  return (
    <div className="min-h-full bg-transparent text-slate-100">
      <div className="mx-auto max-w-6xl px-4 py-6 md:py-10">
        {/* Header + Status */}
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-cyan-300 md:text-2xl">
              Your Dreamframe Collab Hub
            </h2>
            <p className="mt-1 text-sm text-slate-300">
              {hasMatches
                ? 'See who is most likely to help you bring your idea to life, plus your profile snapshot, preferences, and quick actions.'
                : 'Complete your profile and Dreamframe will start surfacing collaborators who can help turn your idea into reality.'}
            </p>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/40 bg-cyan-500/10 px-4 py-1.5 text-xs font-medium text-cyan-200 shadow-sm shadow-cyan-500/30">
            <span className="h-2 w-2 rounded-full bg-emerald-400" />
            CCHI™ Smart Matching{' '}
            {hasMatches ? 'Finding Collaborators' : 'Training on Early Profiles'}
          </div>
        </div>

        {/* Smart Matching & Early Access explainer */}
        <div className="mt-4 rounded-2xl border border-slate-700/60 bg-slate-900/80 px-4 py-3 text-xs text-indigo-100 shadow-lg shadow-fuchsia-500/20 backdrop-blur">
          {hasMatches ? (
            <>
              <span className="font-semibold">
                Smart Matching is live for your account.
              </span>{' '}
              We rank collaborators using the CCHI™ score. As a rule of thumb, matches
              with a score of <span className="font-semibold">5 or higher</span> generally
              indicate stronger creative alignment. As more creators join and update
              their profiles, your recommendations will keep improving automatically.
            </>
          ) : (
            <>
              🧩{' '}
              <span className="font-semibold">
                You’re in Early Access — your Smart Matching Studio is still warming up.
              </span>{' '}
              On Dreamframe, every user can both create and collaborate. Right now we’re
              learning from early profile data. As you add your genres, tools, and work
              style — and as more creators join — your best CCHI™ matches will appear
              here. Scores of <span className="font-semibold">5+</span> usually signal a
              strong creative fit.
            </>
          )}
        </div>

        {/* Top row: Profile / Top Match / Collaboration Preferences */}
        <div className="mt-6 grid gap-4 md:gap-6 lg:grid-cols-3">
          {/* Profile Card */}
          <div className="rounded-2xl border border-slate-700/60 bg-slate-900/80 p-4 shadow-lg shadow-fuchsia-500/10 backdrop-blur">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-sky-200">Your Profile</h3>
              <span className="rounded-full bg-slate-800/80 px-2 py-0.5 text-[10px] uppercase tracking-wide text-slate-300">
                You
              </span>
            </div>
            <div className="mt-4 flex flex-col items-center text-center">
              <img
                src={profile?.avatar || '/user.png'}
                alt="avatar"
                className="h-20 w-20 rounded-full border-2 border-cyan-400/50 object-cover shadow-md shadow-cyan-500/50"
              />
              <div className="mt-3 text-base font-semibold">
                {profile.name || 'Your name'}
              </div>
              <div className="text-xs text-slate-400">
                {profile.role || 'Role not set yet'}
              </div>
            </div>

            <div className="mt-4 space-y-3 text-left">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-sky-300">
                  Preferred Genres
                </div>
                <p className="mt-1 text-xs text-slate-200">
                  {profile.styles || 'Add your favorite genres in your profile.'}
                </p>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-sky-300">
                  Top Skills
                </div>
                <p className="mt-1 text-xs text-slate-200">
                  {profile.skills ||
                    'Highlight your strengths so collaborators can find you.'}
                </p>
              </div>
              {!hasProfileBasics && (
                <div className="mt-2 rounded-xl bg-slate-800/80 px-3 py-2 text-[11px] text-slate-300">
                  🔑 Complete your profile (role, genres, tools, availability) to unlock
                  better matches.
                </div>
              )}
            </div>
          </div>

          {/* Top Match Card */}
          <div className="rounded-2xl border border-slate-700/60 bg-slate-900/80 p-4 shadow-lg shadow-fuchsia-500/10 backdrop-blur">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-violet-200">
                Top Match <span className="font-normal text-xs">by CCHI™</span>
              </h3>
            <span className="rounded-full bg-violet-500/20 px-2 py-0.5 text-[11px] text-violet-100">
                Smart Match
              </span>
            </div>

            {topMatch ? (
              <div className="mt-4 flex flex-col items-center text-center">
                <img
                  src={topMatch?.avatar || '/user.png'}
                  alt="match avatar"
                  className="h-16 w-16 rounded-full border border-violet-400/60 object-cover shadow"
                />
                <div className="mt-2 text-sm font-semibold">{topMatch.name}</div>
                <div className="text-xs text-slate-400">{topMatch.role}</div>

                <div className="mt-3 w-full space-y-1 text-xs text-left">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">CCHI Score</span>
                    <span className="rounded-full bg-violet-500/20 px-2 py-0.5 text-[11px] text-violet-100">
                      {topMatch.score}
                    </span>
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-violet-200">
                      Shared Genres
                    </div>
                    <p className="mt-1 text-[11px] text-slate-200">
                      {topMatch.sharedGenres?.length
                        ? topMatch.sharedGenres.join(', ')
                        : 'No genre overlap yet.'}
                    </p>
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-violet-200">
                      Shared Tools
                    </div>
                    <p className="mt-1 text-[11px] text-slate-200">
                      {topMatch.sharedTools?.length
                        ? topMatch.sharedTools.join(', ')
                        : 'No shared tools yet.'}
                    </p>
                  </div>
                </div>

                <p className="mt-3 text-[11px] text-slate-400">
                  Creators with CCHI™{' '}
                  <span className="font-semibold text-violet-100">5+</span> are more likely
                  to be strong collaborators for your next project.
                </p>

                <button
                  className="mt-4 inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 px-4 py-2 text-xs font-semibold text-white shadow-md shadow-fuchsia-500/40 transition hover:brightness-110"
                  onClick={() => navigate(`/profile/${topMatch.user_id}`)}
                >
                  🔍 View Profile
                </button>
              </div>
            ) : (
              <div className="mt-4 space-y-3 text-xs text-slate-300">
                <p>
                  We don’t have enough overlap yet to show a top CCHI™ match. This is
                  normal in Early Access while profiles are still being created.
                </p>
                <p>
                  Complete your genres, tools, and work style — and as more creators join,
                  we’ll surface collaborators here. Matches with a score of{' '}
                  <span className="font-semibold">5+</span> will be highlighted as stronger
                  fits.
                </p>
              </div>
            )}
          </div>

          {/* Collaboration Preferences */}
          <div className="rounded-2xl border border-slate-700/60 bg-slate-900/80 p-4 shadow-lg shadow-fuchsia-500/10 backdrop-blur">
            <h3 className="text-sm font-semibold text-emerald-200">
              Collaboration Preferences
            </h3>
            <p className="mt-2 text-xs text-slate-300">
              These are <span className="font-semibold">your</span> preferences (not your
              match’s). They help us suggest collaborators who fit your schedule and work
              style.
            </p>

            <div className="mt-4 space-y-3 text-xs">
              <div className="flex items-center justify-between rounded-xl bg-slate-800/80 px-3 py-2">
                <span className="text-slate-300">General Availability</span>
                <span className="rounded-full bg-slate-900/80 px-2 py-0.5 text-[11px] text-emerald-200">
                  {profile.availability || 'Not set'}
                </span>
              </div>
              <div className="flex items-center justify-between rounded-xl bg-slate-800/80 px-3 py-2">
                <span className="text-slate-300">Time Zone</span>
                <span className="rounded-full bg-slate-900/80 px-2 py-0.5 text-[11px] text-emerald-200">
                  {profile.timezone || 'Not set'}
                </span>
              </div>
              <div className="flex items-center justify-between rounded-xl bg-slate-800/80 px-3 py-2">
                <span className="text-slate-300">Work Style</span>
                <span className="rounded-full bg-slate-900/80 px-2 py-0.5 text-[11px] text-emerald-200">
                  {profile.openTo || 'Not set'}
                </span>
              </div>
              {!hasProfileBasics && (
                <p className="text-[11px] text-slate-400">
                  Tip: Setting these makes it easier for animators, writers, and voice
                  artists to understand how you like to work.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Second row: tools + recent project + quick actions */}
        <div className="mt-6 grid gap-4 md:gap-6 lg:grid-cols-3">
          {/* Tools */}
          <div className="rounded-2xl border border-slate-700/60 bg-slate-900/80 p-4 shadow-lg shadow-fuchsia-500/10 backdrop-blur">
            <h3 className="text-sm font-semibold text-sky-200">Top Tools You Use</h3>
            <p className="mt-2 text-xs text-slate-300">
              These help collaborators quickly understand your production stack.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {Array.isArray(profile.tools) && profile.tools.length > 0 ? (
                profile.tools.map((tool, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center rounded-full bg-cyan-500/10 px-3 py-1 text-[11px] font-medium text-cyan-200"
                  >
                    {tool}
                  </span>
                ))
              ) : (
                <p className="mt-2 text-xs text-slate-400">
                  Add your favorite tools (Runway, Pika, Sora, ElevenLabs, etc.) in your
                  profile so matches understand how you work.
                </p>
              )}
            </div>
          </div>

          {/* Most Recent Project */}
          <div className="rounded-2xl border border-slate-700/60 bg-slate-900/80 p-4 shadow-lg shadow-fuchsia-500/10 backdrop-blur">
            <h3 className="text-sm font-semibold text-purple-200">
              Most Recent Project
            </h3>
            {recentProject ? (
              <div className="mt-3 space-y-1 text-xs text-slate-200">
                <p>
                  <span className="font-semibold text-slate-100">Title:</span>{' '}
                  {recentProject.title}
                </p>
                <p>
                  <span className="font-semibold text-slate-100">Your Role:</span>{' '}
                  {recentProject.role}
                </p>
                <p>
                  <span className="font-semibold text-slate-100">Last Updated:</span>{' '}
                  {new Date(recentProject.updated_at).toLocaleDateString()}
                </p>
              </div>
            ) : (
              <p className="mt-3 text-xs text-slate-300">
                You don’t have any projects yet in this workspace. In later stages, you’ll
                see your active productions here once project creation is enabled.
              </p>
            )}
          </div>

          {/* Quick Actions */}
          <div className="rounded-2xl border border-slate-700/60 bg-slate-900/80 p-4 shadow-lg shadow-fuchsia-500/10 backdrop-blur">
            <h3 className="text-sm font-semibold text-amber-200">Quick Actions</h3>
            <ul className="mt-3 space-y-2 text-xs">
              <li
                className="flex cursor-pointer items-center justify-between rounded-xl bg-slate-800/70 px-3 py-2 text-slate-200 transition hover:bg-slate-700/80"
                onClick={() => navigate('/profile/edit')}
              >
                <span>✨ Complete or update your profile</span>
                <span className="text-xs text-slate-400">Profile</span>
              </li>
              {/* REMOVED FOR STAGE 1
              <li
                className="flex cursor-pointer items-center justify-between rounded-xl bg-slate-800/70 px-3 py-2 text-slate-200 transition hover:bg-slate-700/80"
                onClick={() => navigate('/find-creators')}
              >
                <span>🔍 Explore more CCHI™ matches</span>
                <span className="text-xs text-slate-400">Matches</span>
              </li>
              <li
                className="flex cursor-pointer items-center justify-between rounded-xl bg-slate-800/70 px-3 py-2 text-slate-200 transition hover:bg-slate-700/80"
                onClick={() => navigate('/profile-review')}
              >
                <span>👁️ Preview how others see your profile</span>
                <span className="text-xs text-slate-400">Review</span>
              </li>
              */}
            </ul>
            {!hasMatches && (
              <p className="mt-3 text-[11px] text-slate-400">
                Start with your profile. As soon as we have enough overlap between you and
                other creators, your first CCHI™ matches will appear automatically.
              </p>
            )}
          </div>
        </div>

        {/* Matches table */}
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-sky-200">
              Your Top CCHI™ Matches
            </h4>
            <span className="text-[11px] text-slate-400">
              Click a row to open the profile in detail. Scores of 5+ are usually stronger
              matches.
            </span>
          </div>

          <div className="mt-3 overflow-hidden rounded-2xl border border-slate-700/60 bg-slate-900/80 shadow-lg shadow-fuchsia-500/20 backdrop-blur">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-slate-800/90 text-[11px] uppercase tracking-wide text-slate-300">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">CCHI Score</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(profile.cchiMatches) &&
                profile.cchiMatches.length > 0 ? (
                  profile.cchiMatches.map((m, i) => (
                    <tr
                      key={i}
                      className="cursor-pointer border-t border-slate-800/80 transition hover:bg-slate-800/80"
                      onClick={() => navigate(`/profile/${m.user_id}`)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <img
                            src={m.avatar}
                            alt="avatar"
                            className="h-6 w-6 rounded-full object-cover"
                          />
                          <span>{m.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-200">{m.role}</td>
                      <td className="px-4 py-3 text-slate-100">{m.score}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-4 py-4 text-center text-[11px] text-slate-400"
                    >
                      No matches yet. We’re still in Early Access, so CCHI™ needs more
                      creator profiles — including yours — to work well. As you and other
                      creators complete profiles, we’ll automatically surface collaborators
                      here and highlight stronger fits with scores of 5+.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {hasMatches && !hasStrongMatches && (
            <p className="mt-2 text-[11px] text-slate-400">
              You have some early matches, but none are above 5 yet. As more creators join
              and profiles get richer, expect to see higher-scoring collaborators here.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;
